import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MdClose, MdWarningAmber, MdErrorOutline } from 'react-icons/md';
import { getMySubscription } from '../services/subscriptionService';

const MS_DAY = 24 * 60 * 60 * 1000;
const daysUntil = (date) => Math.ceil((new Date(date).getTime() - Date.now()) / MS_DAY);

// Shows a professional reminder popup to the club admin when the paid subscription
// is due tomorrow/today (1 day left) or already overdue. Dismiss is in-memory only,
// so it reappears on the next login/refresh while the condition still holds.
// Trials are handled by TrialBanner; suspended clubs are blocked before this loads.
export default function SubscriptionReminderModal() {
  const [closed, setClosed] = useState(false);

  const { data } = useQuery({
    queryKey: ['my-subscription-reminder'],
    queryFn:  () => getMySubscription().then(r => r.data.subscription),
    retry:    false,
    staleTime: 5 * 60 * 1000,
  });

  if (closed || !data) return null;
  if (data.isTrial || data.isTrialing) return null;   // trial reminders handled elsewhere

  // Use the shared, status-resolved daysRemaining so the popup matches the
  // Subscription page and Superadmin exactly. Fall back to local math if absent.
  const dueDate  = data.subscriptionEndsAt || data.nextRenewalDate || data.periodEnd;
  if (!dueDate) return null;                           // no due date to act on
  const daysLeft = data.daysRemaining != null ? data.daysRemaining : daysUntil(dueDate);

  const overdue     = daysLeft < 0;
  const overdueDays = data.overdueDays || (overdue ? Math.abs(daysLeft) : 0);
  if (!overdue && daysLeft > 1) return null;           // only fire at ≤1 day or overdue

  // Final grace day → red "will be suspended after today" message.
  const finalGraceDay = data.isFinalGraceDay || false;

  const heading = overdue
    ? (finalGraceDay ? 'Final Payment Reminder' : 'Subscription Overdue')
    : 'Subscription Due Soon';

  const message = overdue
    ? (finalGraceDay
        ? 'Final reminder: your subscription will be suspended after today if payment is not completed.'
        : `Your subscription is overdue by ${overdueDays} day${overdueDays === 1 ? '' : 's'}. Please complete payment to avoid suspension.`)
    : daysLeft === 1
      ? 'Your TrainAxis subscription is due tomorrow. Please complete payment to avoid service interruption.'
      : 'Your TrainAxis subscription is due today. Please complete payment to avoid service interruption.';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40"
      onClick={() => setClosed(true)}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-6 py-5 ${overdue ? 'bg-red-600' : 'bg-amber-500'}`}>
          <div className="flex items-start gap-3">
            {overdue
              ? <MdErrorOutline className="text-2xl text-white flex-shrink-0 mt-0.5" />
              : <MdWarningAmber className="text-2xl text-white flex-shrink-0 mt-0.5" />}
            <div className="flex-1">
              <h2 className="text-white font-bold text-lg leading-tight">{heading}</h2>
            </div>
            <button
              onClick={() => setClosed(true)}
              className="text-white/80 hover:text-white p-1 -mr-1 -mt-1 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <MdClose className="text-xl" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm text-secondary leading-relaxed">{message}</p>

          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              onClick={() => setClosed(true)}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-colors"
            >
              Dismiss
            </button>
            <Link
              to="/admin/subscription"
              onClick={() => setClosed(true)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold text-white transition-colors ${overdue ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primary-dark'}`}
            >
              {overdue ? 'Complete Payment' : 'Manage Subscription'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
